// Lab catalogue + lab-request workflow types (original-chart Phase 3.a).
//
// See migration 0101_lab_catalogue_and_workflow.sql. Lab orders ride on
// the existing service_requests table (service_type='lab') but carry
// their own parallel lab_status column rather than extending
// service_request_status. This keeps procedure code paths byte-identical
// and makes rollback a clean drop-column.

// ─── Lab test catalogue ──────────────────────────────────────────────────

export interface LabTest {
    id: string;
    clinic_id: string;
    code: string;
    name: string;
    specimen_type: string | null;
    turnaround_hours: number | null;
    price_kobo: number | null;
    is_placeholder_price: boolean;
    active: boolean;
    created_at: string;
    updated_at: string;
    created_by_clinician_id: string | null;
    updated_by_clinician_id: string | null;
    deleted_at: string | null;
}

export interface UpsertLabTestRequest {
    id?: string;              // present => update; absent => create
    code: string;
    name: string;
    specimen_type?: string | null;
    turnaround_hours?: number | null;
    price_kobo?: number | null;
    is_placeholder_price?: boolean;
    active?: boolean;
}

export interface LabTestRes {
    test: LabTest;
}

export interface LabTestsListRes {
    tests: LabTest[];
}

// ─── Lab-request workflow ────────────────────────────────────────────────

export type LabStatus =
    | 'ordered'
    | 'collected'
    | 'processing'
    | 'result_entered'
    | 'released'
    | 'cancelled';

export const LAB_STATUS_LABELS: Record<LabStatus, string> = {
    ordered:        'Ordered',
    collected:      'Specimen collected',
    processing:     'Processing',
    result_entered: 'Result ready',
    released:       'Released to patient',
    cancelled:      'Cancelled',
};

// Shape of result_payload on service_requests. Freeform jsonb — a lab
// result can be a numeric panel (FBC) or a boolean (malaria RDT positive)
// or a narrative (urine microscopy). Keep the schema permissive: a map of
// analyte -> { value, unit?, flag?, ref_low?, ref_high?, note? } plus an
// optional free-text summary and attachment list.
export interface LabResultAnalyte {
    value: string | number | boolean;
    unit?: string | null;
    flag?: 'low' | 'high' | 'critical' | 'normal' | null;
    ref_low?: number | null;
    ref_high?: number | null;
    note?: string | null;
}

export interface LabResultPayload {
    analytes?: Record<string, LabResultAnalyte>;
    summary?: string | null;
    attachments?: Array<{ storage_path: string; label?: string | null }>;
}

// A lab-order row — one service_requests row with service_type='lab'
// hydrated for the lab queue screen.
export interface LabRequestRow {
    id: string;
    encounter_id: string;
    patient_id: string;
    clinic_id: string;
    lab_test_id: string | null;
    code_at_order: string;             // snapshot: service_requests.code
    name_at_order: string;             // snapshot: service_requests.description
    specimen_type: string | null;
    lab_status: LabStatus;
    requested_at: string;
    requested_by_clinician_id: string | null;
    collected_at: string | null;
    collected_by_clinician_id: string | null;
    processing_started_at: string | null;
    processing_started_by_clinician_id: string | null;
    result_payload: LabResultPayload | null;
    result_entered_at: string | null;
    result_entered_by_clinician_id: string | null;
    released_at: string | null;
    released_by_clinician_id: string | null;
    lab_cancelled_reason: string | null;
    created_at: string;
    updated_at: string;
}

// Hydrated for the queue board — joins patient + requester name.
export interface LabRequestView extends LabRequestRow {
    patient_full_name: string | null;
    patient_phone_e164: string | null;
    hospital_number: string | null;
    requested_by_full_name: string | null;
    collected_by_full_name: string | null;
    result_entered_by_full_name: string | null;
    released_by_full_name: string | null;
}

export interface OrderLabRequest {
    encounter_id: string;
    lab_test_id: string;
    // Optional override — defaults to the catalogue row's specimen_type.
    specimen_type?: string | null;
    // Optional clinical note surfaced to the lab staff.
    note?: string | null;
}

export interface AdvanceLabRequest {
    // No body for simple state advances (collect / process). The id is
    // in the URL.
    note?: string | null;
}

export interface EnterLabResultRequest {
    result_payload: LabResultPayload;
}

export interface ReleaseLabRequest {
    // Optional note added to the audit log entry.
    note?: string | null;
}

export interface CancelLabRequest {
    reason: string;
}

export interface LabRequestRes {
    request: LabRequestView;
}

export interface LabsQueueRes {
    requests: LabRequestView[];
}
